export const WITHDRAWAL_METHOD_OPTIONS = [
  "ACH",
  "Wire Transfer",
  "Check",
  "Other",
];

export const DEPOSIT_METHOD_OPTIONS = [
  "ACH",
  "Wire Transfer",
  "Check",
  "Other",
];

export const BANK_ACCOUNT_TYPE_OPTIONS = [
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
];

export const REGISTRATION_OPTIONS = [
  "Individual",
  "Joint",
  "Retirement",
  "Inherited Retirement",
  "Trust",
  "Business",
  "UTMA/UGMA",
  "529",
  "Other",
];

export const RETIREMENT_ACCOUNT_TYPES = ["retirement", "inherited retirement"];
export const INDI_JOINT_ACCOUNT_TYPES = ["individual", "joint"];

export const REGISTRATION_TO_ACCOUNT_TYPES = {
  Individual: ["Individual"],
  Joint: [
    "Joint Tenants with Rights of Survivorship (JTWROS)",
    "Joint Tenants in Common (JTIC)",
    "Community Property",
    "Tenants in Entirety",
  ],
  Retirement: [
    "Traditional IRA",
    "Rollover IRA",
    "Roth IRA",
    "Simple IRA",
    "SEP IRA",
  ],
  "Inherited Retirement": ["Inherited IRA", "Inherited Roth IRA"],
  Trust: ["Revocable Trust", "Irrevocable Trust"],
  Business: [
    "Corporation",
    "LLC",
    "Partnership",
    "Unincorporated Business",
    "Sole Proprietorship",
  ],
  "UTMA/UGMA": ["UTMA/UGMA"],
  "529": ["529"],
  Other: [
    "Conservator, Guardian & Estate",
    "Health Savings Account (HSA)",
    "Fidelity NonPrototype Retirement",
    "Money Purchase Plan",
    "Profit Sharing Plan",
    "Self Employed 401(k) & Self Employed 401(k) Roth",
  ],
};

export const getAccountTypesForRegistration = (registration) =>
  REGISTRATION_TO_ACCOUNT_TYPES[registration] ||
  REGISTRATION_TO_ACCOUNT_TYPES.Other;

export const PROCEEDS_OPTIONS = [
  "Hold in Core Account Investment Vehicle",
  "Send by Check to Mailing Address of Account",
  "Send by External Bank Link",
  "Send by Autosweep",
  "Delivery vs. Payment",
];

export const SECURITY_PURCHASES_OPTIONS = [
  "Hold in Street Name",
  "Register Certificates to Account Name and Send to Mailing Address",
  "Delivery vs. Payment",
];

export const DIVIDEND_OPTIONS = [
  "Reinvest: Payments from all mutual funds",
  "Reinvest: Payments from all eligible securities; any payments from ineligible securities will be paid to your core account",
  "Reinvest: Payments from all eligible U.S. equities and closed-end funds",
  "Pay All to Account: Do not distribute by check",
];

export const DEFAULT_DIVIDEND_OPTION = DIVIDEND_OPTIONS[2];

export const TYPE_OF_TRANSFER_OPTIONS = [
  "Brokerage or Trust Company",
  "Mutual Fund Company Transfer",
  "Bank or Credit Union Transfer",
  "Annuity Transfer",
  "Transfer Agent Transfer",
  "Broker Dealer Change Form",
];

export const TRANSFER_EXTENT_OPTIONS = ["Full", "Partial"];

export const FREQUENCY_OPTIONS = [
  "Monthly",
  "Quarterly",
  "Semi-Annually",
  "Annually",
  "One Time",
];

export const DESTINATION_TYPE_OPTIONS = ["Bank Account", "Brokerage Account"];

export const LIQUIDITY_NEEDS_OPTIONS = [
  "None",
  "$0-49,999",
  "$50,000-99,999",
  "$100,000-249,999",
  "Over $250,000",
];

export const LIQUIDITY_NEEDS_MAP = {
  None: { start: 0, end: 0 },
  "$0-49,999": { start: 0, end: 49999 },
  "$50,000-99,999": { start: 50000, end: 99999 },
  "$100,000-249,999": { start: 100000, end: 249999 },
  "Over $250,000": { start: 250000, end: 999999999 },
};

/**
 * Year bands for (1) liquidity_requirements.time_horizon and (2) account risk_tolerance.time_horizon.
 * Same labels/ranges as BFS_MAPPING; not the same as household portfolio TIME_HORIZON_* in householdConstants.
 */
export const LIQUIDITY_TIME_HORIZON_OPTIONS = [
  "None",
  "0-1 Year",
  "1-3 Year",
  "3+ Year",
];

export const LIQUIDITY_TIME_HORIZON_MAP = {
  None: { start: 0, end: 0 },
  "0-1 Year": { start: 0, end: 1 },
  "1-3 Year": { start: 1, end: 3 },
  "3+ Year": { start: 3, end: 100 },
};

export const parseLiquidityNeedsDisplay = (displayValue) => {
  if (!displayValue) return LIQUIDITY_NEEDS_MAP[LIQUIDITY_NEEDS_OPTIONS[0]];
  return LIQUIDITY_NEEDS_MAP[displayValue] || LIQUIDITY_NEEDS_MAP[LIQUIDITY_NEEDS_OPTIONS[0]];
};

export const parseLiquidityTimeHorizonDisplay = (displayValue) => {
  if (!displayValue) return LIQUIDITY_TIME_HORIZON_MAP[LIQUIDITY_TIME_HORIZON_OPTIONS[0]];
  return (
    LIQUIDITY_TIME_HORIZON_MAP[displayValue] ||
    LIQUIDITY_TIME_HORIZON_MAP[LIQUIDITY_TIME_HORIZON_OPTIONS[0]]
  );
};

export const getLiquidityNeedsLabel = (amountRange, fallback = "") => {
  if (!amountRange || amountRange.start === null || amountRange.start === undefined || amountRange.end === null || amountRange.end === undefined) {
    return fallback;
  }
  const s = parseInt(amountRange.start, 10) || 0;
  const e = parseInt(amountRange.end, 10) || 0;
  const [none, low, mid, high, over] = LIQUIDITY_NEEDS_OPTIONS;
  if (s === 0 && e === 0) return none;
  if (e <= LIQUIDITY_NEEDS_MAP[low].end) return low;
  if (e <= LIQUIDITY_NEEDS_MAP[mid].end) return mid;
  if (e <= LIQUIDITY_NEEDS_MAP[high].end) return high;
  return over;
};

export const getLiquidityTimeHorizonLabel = (timeHorizonRange, fallback = "") => {
  if (!timeHorizonRange || timeHorizonRange.start === null || timeHorizonRange.start === undefined || timeHorizonRange.end === null || timeHorizonRange.end === undefined) {
    return fallback;
  }
  const s = parseInt(timeHorizonRange.start, 10) || 0;
  const e = parseInt(timeHorizonRange.end, 10) || 0;
  const [none, y01, y13, y3p] = LIQUIDITY_TIME_HORIZON_OPTIONS;
  if (s === 0 && e === 0) return none;
  if (e <= LIQUIDITY_TIME_HORIZON_MAP[y01].end) return y01;
  if (e <= LIQUIDITY_TIME_HORIZON_MAP[y13].end) return y13;
  return y3p;
};

const ACCOUNT_OPTION_ALIASES = {
  "one time": "One Time",
  wire: "Wire Transfer",
  "hold in core": "Hold in Core Account Investment Vehicle",
  "hold in core account investment vehicle": "Hold in Core Account Investment Vehicle",
  "send by check to mailing address of account": "Send by Check to Mailing Address of Account",
  "send by external bank link": "Send by External Bank Link",
  "send by autosweep": "Send by Autosweep",
  "delivery vs. payment": "Delivery vs. Payment",
  "hold in street name": "Hold in Street Name",
  "register certificates to account name and send to mailing address":
    "Register Certificates to Account Name and Send to Mailing Address",
};

export const normalizeAccountOptionValue = (value) => {
  if (value === null || value === undefined) return value;
  const raw = String(value).trim();
  if (!raw) return raw;
  return ACCOUNT_OPTION_ALIASES[raw.toLowerCase()] || raw;
};

export const TRUST_TAX_ID_TYPE_OPTIONS = ["SSN", "TIN"];

export const TAX_CLASSIFICATION_BY_ACCOUNT_TYPE = {
  Corporation: ["C-corp", "S-corp"],
  LLC: ["C-corp", "S-corp", "Partnership", "Sole Proprietorship"],
};

export const getTaxClassificationOptions = (accountType) =>
  TAX_CLASSIFICATION_BY_ACCOUNT_TYPE[accountType] || [];

export const INHERITED_ACCOUNT_TYPES = ["Inherited IRA", "Inherited Roth IRA"];

export const ESTATE_ACCOUNT_TYPES = ["Conservator, Guardian & Estate"];

export const BANK_LINK_TYPE_OPTIONS = WITHDRAWAL_METHOD_OPTIONS;
